import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Utensils, Clock, Heart, CheckCircle, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MindfulEatingGuide() {
  const navigate = useNavigate();

  const principles = [
    { title: "Eat Without Distractions", desc: "No phone, TV, or reading. Just you and your food." },
    { title: "Chew Thoroughly", desc: "Aim for 20-30 chews per bite. Digestion starts in the mouth." },
    { title: "Put Your Fork Down", desc: "Between bites, rest your utensils. Slow the pace." },
    { title: "Notice Textures & Flavors", desc: "What do you taste? Sweet, salty, savory, bitter?" },
    { title: "Check Your Hunger", desc: "Pause mid-meal and ask: Am I still hungry or just eating?" },
    { title: "Honor Fullness", desc: "Stop when satisfied, not stuffed. You can always eat more later." }
  ];

  const hungerScale = [
    { level: 1, desc: "Starving, weak, dizzy" },
    { level: 2, desc: "Very hungry, irritable" },
    { level: 3, desc: "Hungry, stomach growling" },
    { level: 4, desc: "Slightly hungry" },
    { level: 5, desc: "Neutral, neither hungry nor full" },
    { level: 6, desc: "Satisfied, comfortable" },
    { level: 7, desc: "Full, slight pressure" },
    { level: 8, desc: "Stuffed, uncomfortable" },
    { level: 9, desc: "Very stuffed, bloated" },
    { level: 10, desc: "Painfully full, nauseous" }
  ];

  const benefits = [
    "30% reduction in overeating",
    "Better digestion and nutrient absorption",
    "Greater meal satisfaction with less food",
    "Reduced bloating and discomfort",
    "Healthier relationship with food",
    "Natural weight management"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            Week 2 • Day 6 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Mindful Eating Practice
          </h1>
          <p className="text-xl text-secondary-600">Transform Your Relationship with Food</p>
        </div>

        {/* Today's Challenge */}
        <section className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Utensils className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Practice</h3>
              <p className="text-teal-100 text-lg">
                Choose one meal today to eat in complete silence. No phone, no TV, no reading. 
                Chew each bite 20 times. Really taste your food. Notice when you start feeling satisfied.
              </p>
            </div>
          </div>
        </section>

        {/* What is Mindful Eating */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">What is Mindful Eating?</h2>
          <p className="text-secondary-700 mb-4">
            Mindful eating is paying full attention to the experience of eating—noticing colors, smells, textures, 
            flavors, temperatures, and even sounds. It's also being aware of your body's hunger and fullness cues, 
            eating when hungry and stopping when satisfied.
          </p>
          <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
            <p className="text-secondary-800 flex items-start gap-2">
              <Heart className="w-5 h-5 text-teal-500 mt-1 flex-shrink-0" />
              <span><strong>Key insight:</strong> Most of us eat on autopilot. We're watching TV, scrolling phones, or rushing. 
              This disconnection leads to overeating, poor digestion, and missing the joy of food.</span>
            </p>
          </div>
        </section>

        {/* Principles */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">The 6 Principles</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {principles.map((principle, index) => (
              <div key={index} className="p-4 bg-teal-50 rounded-xl">
                <h3 className="font-bold text-teal-700 mb-1 flex items-center gap-2">
                  <Pause className="w-4 h-4" />
                  {principle.title}
                </h3>
                <p className="text-sm text-secondary-600">{principle.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Hunger Scale */}
        <section className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 border-2 border-teal-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">The Hunger-Fullness Scale</h2>
          <p className="text-secondary-600 mb-4">Learn to recognize your body's signals. Ideally, eat when you're at 3-4 and stop at 6-7.</p>
          <div className="grid md:grid-cols-2 gap-2">
            {hungerScale.map((item) => (
              <div 
                key={item.level} 
                className={`flex items-center gap-3 p-2 rounded-lg ${
                  item.level >= 3 && item.level <= 7 ? 'bg-teal-100' : 'bg-white'
                }`}
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  item.level >= 3 && item.level <= 7 ? 'bg-teal-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {item.level}
                </span>
                <span className="text-sm text-secondary-700">{item.desc}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Benefits of Mindful Eating</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2 p-3 bg-teal-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                <span className="text-secondary-700">{benefit}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default MindfulEatingGuide;
